import { useId, type ReactNode } from "react";
import { Inbox } from "lucide-react";
import { errorMessage } from "../../lib/error-message";
import { useQueryErrorToast } from "./use-query-error-toast";
import { useTranslation } from "../../i18n";
import { cn } from "../../lib/utils";
import { Button } from "./button";
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from "./empty";
import { Skeleton } from "./skeleton";

export function EmptyState({ title, detail, compact = false }: Readonly<{ title: string; detail?: string; compact?: boolean }>) {
  return <Empty className={cn("empty-state", compact ? "min-h-32" : "min-h-[220px] p-[30px]")}>
    <EmptyHeader>
      <Inbox aria-hidden="true" className="mb-1 size-8 text-muted-foreground/60" />
      <EmptyTitle>{title}</EmptyTitle>
      {detail ? <EmptyDescription>{detail}</EmptyDescription> : null}
    </EmptyHeader>
  </Empty>;
}

export function ErrorState({ error, onRetry }: Readonly<{ error: unknown; onRetry?: () => void }>) {
  const { t } = useTranslation();
  const message = errorMessage(error);
  return <div className="notice error-notice flex flex-wrap items-center justify-between gap-3" role="alert">
    <span>{message}</span>
    {onRetry ? <Button variant="outline" size="sm" onClick={onRetry}>{t("workspace.retry")}</Button> : null}
  </div>;
}

/** One placeholder row matching the list/grid row shape, for fixed-height paging. */
export function ListRowSkeleton({ layout = "list" }: Readonly<{ layout?: "list" | "grid" }>) {
  return <div aria-hidden="true" className={cn("flex gap-4 py-4", layout === "grid" && "flex-col rounded-lg border border-border p-4")}>
    <Skeleton className={layout === "grid" ? "aspect-[4/3] w-full" : "size-24 shrink-0 sm:size-36"} />
    <div className="min-w-0 flex-1 space-y-3 py-1"><Skeleton className="h-4 w-2/3" /><Skeleton className="h-4 w-5/6" /><Skeleton className="h-3 w-1/2" /></div>
  </div>;
}

export function ListSkeleton({ layout = "list" }: Readonly<{ layout?: "list" | "grid" }>) {
  return <div aria-hidden="true" className={layout === "grid" ? "grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4" : "divide-y divide-border border-y border-border"}>
    {Array.from({ length: layout === "grid" ? 6 : 5 }, (_, index) => <ListRowSkeleton key={index} layout={layout} />)}
  </div>;
}

export function ListState({ hasItems, loading = false, refreshing = false, error, errorToastId, onRetry, skeleton, empty, children }: Readonly<{
  hasItems: boolean;
  loading?: boolean;
  refreshing?: boolean;
  error?: unknown;
  errorToastId?: string;
  onRetry?: () => void;
  skeleton?: ReactNode;
  empty: ReactNode;
  children: ReactNode;
}>) {
  const { t } = useTranslation();
  const notificationId = useId();
  useQueryErrorToast(hasItems ? error : null, errorMessage(error), errorToastId ?? notificationId);
  if (loading && !hasItems) return <div role="status" aria-busy="true"><span className="sr-only">{t("common.loading")}</span>{skeleton ?? <ListSkeleton />}</div>;
  if (error && !hasItems) return <ErrorState error={error} onRetry={onRetry} />;
  return <div aria-busy={refreshing} className="relative">
    {hasItems ? children : empty}
  </div>;
}
